import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { submitEnquiry } from '../services/api';

const Enquiry = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', subject: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitEnquiry(formData);
      toast.success('Your enquiry has been submitted successfully! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white">
      <section className="bg-[#2E3A6F] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold">Send Us an Enquiry</h1>
            <p className="text-base md:text-lg text-white/90">
              Have questions? We're here to help. Fill out the form and we'll get back to you shortly.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="space-y-6">
              <Card className="border-0 bg-white hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#2E3A6F]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#2E3A6F]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                      <p className="text-gray-600 text-sm">+91 78883 32031</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#2E3A6F]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#2E3A6F]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <p className="text-gray-600 text-sm">sales@namohtoolings.in</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#2E3A6F]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#2E3A6F]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                      <p className="text-gray-600 text-sm">
                        K-237,238,PHASE VIII FOCAL POINT<br />
                        LUDHIANA, PUNJAB 141010(INDIA)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-br from-[#2E3A6F] to-[#1f2747] text-white">
                <CardContent className="p-6 space-y-3">
                  <h3 className="text-xl font-semibold">Business Hours</h3>
                  <div className="space-y-2 text-sm text-white/90">
                    <p className="flex justify-between"><span>Monday - Saturday:</span><span>9:30 AM - 6:00 PM</span></p>
                    <p className="flex justify-between"><span>Sunday:</span><span>Closed</span></p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2">
              <Card className="border-0 bg-white">
                <CardHeader><CardTitle className="text-2xl">Enquiry Form</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="enquiry-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input id="name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" data-testid="enquiry-name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input id="email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" data-testid="enquiry-email" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input id="phone" type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="enquiry-phone" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name</Label>
                        <Input id="company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Your Company" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input id="subject" required value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Product enquiry, pricing, partnership, etc." data-testid="enquiry-subject" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea id="message" required value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us more about your requirements..." rows={6} data-testid="enquiry-message" />
                    </div>
                    <Button type="submit" size="lg" className="w-full bg-[#2E3A6F] hover:bg-[#1f2747]" disabled={submitting} data-testid="enquiry-submit-btn">
                      <Send className="mr-2" size={20} />
                      {submitting ? 'Submitting...' : 'Submit Enquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enquiry;
